/**
 * Exhibits / References (Summary Results tab): the population and IPE proof files, then every evidence file in sample
 * order, each with its exhibit number, the attributes it supports and its status. Shared by the tab and the working
 * paper (PDF / Excel).
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import { numberExhibits } from '../../../components/attribute-result/exhibitNumbering.js';

export interface Exhibit {
  label: string;             // E-Pop, E-IPE, E-1 ...
  file: string;
  fileId: number | null;
  locator: any;
  type: string | null;       // 'Population File' / 'IPE Proof' for the population files
  source: string | null;     // evidence-map source: ai_mapped, manual, missing, population, ipe_proof ...
  attributes: string[];      // attribute refs the file supports
  status: string;            // Missing, Incomplete, Population, IPE Proof, Available
}

const normKey = (r: any) => (typeof r === 'string' ? r.replace(/^["']+|["']+$/g, '').trim().toLowerCase().replace(/\s+/g, ' ') : '');
const normRef = (r: any) => (typeof r === 'string' ? r.replace(/^["']+|["']+$/g, '').trim() : r);

/** The status shown for a file, from where its mapping came from. */
export function exhibitStatus(source?: string | null): string {
  if (!source || source === 'missing') return 'Missing';
  if (source === 'ai_mapped') return 'Incomplete';
  if (source === 'population') return 'Population';
  if (source === 'ipe_proof') return 'IPE Proof';
  return 'Available';
}

export function buildExhibits(evidenceMap: any[], attributes: any[], run: any): { population: Exhibit[]; files: Exhibit[] } {
  const info = new Map<string, { fileName: string; attrRefs: Set<string>; sampleRefs: Set<string>; source: string | null; fileId: number | null; locator: any }>();
  (evidenceMap || []).forEach((c) => {
    const key = c.file_name || c.fileName || 'Unknown file';
    if (!info.has(key)) {
      info.set(key, { fileName: key, attrRefs: new Set(), sampleRefs: new Set(), source: c.source ?? null, fileId: c.file_id ?? null, locator: c.locator });
    }
    const sRef = normKey(c.sample_ref || '');
    if (sRef) info.get(key)!.sampleRefs.add(sRef);
    const direct = c.attribute_ref || c.attribute_id || '';
    if (direct) info.get(key)!.attrRefs.add(direct);
  });
  // An attribute is supported by a file its sample results cite, or that is mapped to one of its samples.
  (attributes || []).forEach((attr) => (attr.sample_results || []).forEach((sr: any) => {
    const cited = sr?.citation?.source_file || '';
    if (cited && info.has(cited)) info.get(cited)!.attrRefs.add(attr.attribute_ref);
    const sRef = normKey(sr.record_ref || '');
    if (sRef) info.forEach((f) => { if (f.sampleRefs.has(sRef)) f.attrRefs.add(attr.attribute_ref); });
  }));

  const sampleOrder = [...new Set((attributes || []).flatMap((a) => (a.sample_results || []).map((sr: any) => normRef(sr.record_ref))))];
  const { order, labels } = numberExhibits(evidenceMap, sampleOrder);
  const orderedNames = [...order.filter((fn: string) => info.has(fn)), ...[...info.keys()].filter((fn) => !order.includes(fn))];
  const list = orderedNames.length > 0 ? orderedNames.map((fn) => info.get(fn)!) : [...info.values()];
  const files: Exhibit[] = list.map((f, i) => ({
    label: labels.get(f.fileName) || `E-${i + 1}`,
    file: f.fileName, fileId: f.fileId, locator: f.locator, type: null, source: f.source,
    attributes: [...f.attrRefs], status: exhibitStatus(f.source),
  }));

  const population = run?.population || {};
  const pop: Exhibit[] = [];
  if (population.population_file_name) {
    pop.push({ label: 'E-Pop', file: population.population_file_name, fileId: population.population_file_id || null, locator: null,
      type: 'Population File', source: 'population', attributes: [], status: exhibitStatus('population') });
  }
  if (population.ipe_proof_file_name) {
    pop.push({ label: 'E-IPE', file: population.ipe_proof_file_name, fileId: population.ipe_proof_file_id || null, locator: null,
      type: 'IPE Proof', source: 'ipe_proof', attributes: [], status: exhibitStatus('ipe_proof') });
  }
  return { population: pop, files };
}
